import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Activity } from "lucide-react";
import { toast } from "sonner";
import MarketPicker from "@/components/market/MarketPicker";
import TimeframeSwitch from "@/components/market/TimeframeSwitch";
import AnalyzeButton from "@/components/market/AnalyzeButton";
import ChartViewer from "@/components/market/ChartViewer";
import MarketAnalyzingView from "@/components/market/MarketAnalyzingView";
import MarketHistoryPanel from "@/components/market/MarketHistoryPanel";
import MarketResultSimple from "@/components/market/MarketResultSimple";
import {
  apiMarketCandles,
  apiAnalyzeMarket,
  formatApiError,
} from "@/lib/api";

/**
 * Market — Phase 4: live chart + Analyze → ResultsView → History.
 *
 * Explicit `exchange` and `market_type` are first-class from day one:
 * every backend call includes them and every stored result carries them
 * in `market_context`.
 *
 * Stages:
 *   "viewer"    → user picks pair + timeframe, sees the live chart
 *   "analyzing" → POST /market-analysis/analyze is in flight
 *   "results"   → ResultsView with LIVE MARKET badge + Run Another CTA
 *
 * History panel is always mounted; it refreshes when a new analysis
 * completes (bump `historyKey`) or when the user opens a past one.
 */
export default function Market() {
  const [exchange] = useState("binance");
  const [marketType] = useState("spot");
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [timeframe, setTimeframe] = useState("1H");

  // Chart viewer state
  const [candles, setCandles] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);

  // Analyze flow state
  const [stage, setStage] = useState("viewer"); // "viewer" | "analyzing" | "results"
  const [result, setResult] = useState(null);
  const [marketContext, setMarketContext] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    if (!symbol || !timeframe) return;
    let cancelled = false;
    (async () => {
      setChartLoading(true);
      setChartError(null);
      try {
        const d = await apiMarketCandles({
          exchange, market_type: marketType, symbol, timeframe, limit: 1200,
        });
        if (!cancelled) setCandles(d.candles || []);
      } catch (e) {
        if (!cancelled) {
          setChartError(formatApiError(e));
          setCandles([]);
        }
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [exchange, marketType, symbol, timeframe]);

  const analyzeReady = candles.length >= 100 && !chartLoading && !chartError;

  const handleAnalyze = async () => {
    if (!analyzeReady) return;
    setStage("analyzing");
    setResult(null);
    try {
      const data = await apiAnalyzeMarket({
        exchange, market_type: marketType, symbol, timeframe, limit: 1200,
      });
      setResult(data);
      // Backend attaches market_context to the persisted doc but the
      // POST response is the AnalysisResult only — we know the request
      // shape, so mirror it for the badge without another round-trip.
      setMarketContext({
        exchange, market_type: marketType, symbol, timeframe,
      });
      setStage("results");
      setHistoryKey((k) => k + 1);
    } catch (e) {
      const status = e?.response?.status;
      const detail = formatApiError(e);
      // Actionable, status-aware toasts. "Wait / no clean setup" is NOT
      // an error — those still return 200 with the AnalysisResult and
      // land in the results stage as intended.
      if (status === 401) {
        toast.error("Your session expired. Please log in again.");
      } else if (status === 402 || status === 403) {
        toast.error("Membership required to run a live market analysis.");
      } else if (status === 400) {
        toast.error(`Bad request: ${detail}`);
      } else if (status === 502) {
        toast.error(
          "The market analysis engine is temporarily unavailable. Please retry in a moment.",
          { description: detail }
        );
      } else if (status === 500) {
        toast.error("Server error while running the analysis.", { description: detail });
      } else if (e?.code === "ECONNABORTED") {
        toast.error("The analysis timed out. Try again — the engine can be slow on higher timeframes.");
      } else {
        toast.error(detail || "Analysis failed. Please try again.");
      }
      setStage("viewer");
    }
  };

  const handleOpenHistoryRow = (row) => {
    // History rows carry the full AnalysisResult + market_context.
    setResult(row);
    setMarketContext(row.market_context || {
      exchange: "binance", market_type: "spot",
      symbol: row.asset_guess, timeframe: row.timeframe_guess,
    });
    setStage("results");
    // Also sync the selectors so returning to viewer stays consistent.
    if (row?.market_context?.symbol) setSymbol(row.market_context.symbol);
    if (row?.market_context?.timeframe) setTimeframe(row.market_context.timeframe);
  };

  const resetToViewer = () => {
    setResult(null);
    setStage("viewer");
  };

  return (
    <section className="pt-10 pb-20" data-testid="market-page">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
          <Activity className="w-3.5 h-3.5" /> ── live market ──
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white">
          AI Market Analysis
        </h1>
        <p className="text-sm text-neutral-400 mt-1.5">
          Chart-first analysis on live Binance Spot data.
          <span className="text-neutral-600"> · exchange={exchange} · market_type={marketType}</span>
        </p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6 items-start">
          {/* Main panel — viewer / analyzing / results */}
          <div className="min-w-0">
            <AnimatePresence mode="wait">
              {stage === "viewer" && (
                <motion.div
                  key="viewer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border border-neutral-900 bg-[#0a0a0a]"
                >
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-neutral-900">
                    <MarketPicker value={symbol} onChange={setSymbol} />
                    <TimeframeSwitch value={timeframe} onChange={setTimeframe} />
                    <div className="ml-auto">
                      <AnalyzeButton
                        disabled={!analyzeReady}
                        loading={false}
                        onClick={handleAnalyze}
                      />
                    </div>
                  </div>
                  <div className="p-2">
                    <ChartViewer
                      candles={candles}
                      loading={chartLoading}
                      error={chartError}
                      height={520}
                    />
                  </div>
                  <div className="px-4 py-2 border-t border-neutral-900 flex flex-wrap items-center gap-4 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                    <span data-testid="market-status">
                      {chartLoading ? "fetching…" : `${candles.length} candles`}
                    </span>
                    <span>·</span>
                    <span>{symbol} {timeframe}</span>
                    <span>·</span>
                    <span>binance spot</span>
                  </div>
                </motion.div>
              )}

              {stage === "analyzing" && (
                <motion.div
                  key="analyzing"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <MarketAnalyzingView symbol={symbol} timeframe={timeframe} />
                </motion.div>
              )}

              {stage === "results" && result && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <MarketResultSimple
                    result={result}
                    marketContext={marketContext}
                    onReset={resetToViewer}
                    resetLabel="Back to chart"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar — history */}
          <div className="lg:sticky lg:top-4">
            <MarketHistoryPanel
              onSelect={handleOpenHistoryRow}
              refreshKey={historyKey}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
